import { ISaSmsAuditEntry, ISaSmsPage } from '@interfaces/sa-sms';
import { Card, Table, Tag } from 'antd';
import { useSaSmsGet } from 'hooks/use-sa-sms-api';
import { useSaSmsUserNames } from 'hooks/use-sa-sms-user-names';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 25;

/** Console write history of one tenant: who did what, when, and why (portal-api `sms_admin_audit`). */
export const SmsAdminAuditTable: React.FC<{ tenantId: string; refreshKey: number }> = ({
    tenantId,
    refreshKey,
}) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const [page, setPage] = useState(1);
    const { data, isLoading, refetch } = useSaSmsGet<ISaSmsPage<ISaSmsAuditEntry>>(
        `/tenants/${tenantId}/audit`,
        { page, pageSize: PAGE_SIZE },
    );
    // A write elsewhere on the page bumps refreshKey -> reload.
    useEffect(() => {
        if (refreshKey > 0) refetch();
    }, [refreshKey, refetch]);
    const names = useSaSmsUserNames((data?.items ?? []).map((e) => e.actorUserId));
    return (
        <Card title={t('activity.audit')}>
            <Table<ISaSmsAuditEntry>
                rowKey="id"
                size="small"
                loading={isLoading}
                dataSource={data?.items ?? []}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: data?.total ?? 0,
                    showSizeChanger: false,
                    onChange: setPage,
                }}
                scroll={{ x: 700 }}
            >
                <Table.Column<ISaSmsAuditEntry>
                    title={t('credits.date')}
                    render={(_, e) => new Date(e.createdAt).toLocaleString(i18n.language)}
                />
                <Table.Column<ISaSmsAuditEntry>
                    title={t('credits.actor')}
                    render={(_, e) => names[e.actorUserId] ?? e.actorUserId}
                />
                <Table.Column<ISaSmsAuditEntry>
                    title={t('activity.action')}
                    render={(_, e) => (
                        <Tag>{t(`audit.${e.action}`, { defaultValue: e.action })}</Tag>
                    )}
                />
                <Table.Column<ISaSmsAuditEntry> title={t('credits.reason')} dataIndex="reason" />
            </Table>
        </Card>
    );
};
